import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { updateFormData, nextStep, prevStep } from "../../redux/formSlice";

import {
  Box,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import Hour1 from "../../components/icons/Hour1";
import Hour12 from "../../components/icons/Hour12";
import Hour6 from "../../components/icons/Hour6";
import Hour24 from "../../components/icons/Hour24";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import StepNavigation from "../../components/StepNavigation";

function FileName() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { register, handleSubmit } = useForm();
  const formData = useSelector((state) => state.form.formData);

  const [selectedTime, setSelectedTime] = useState(formData.time); // Track selected time
  const [selectedName, setSelectedName] = useState(formData.name || ""); // Default to formData.name or empty
  const [nameError, setNameError] = useState(false); // Error state for the TextField

  const handleRadioChange = (event) => {
    setSelectedTime(event.target.value);
  };

  const handleNameChange = (event) => {
    setSelectedName(event.target.value);

    if (event.target.value.trim() === "") {
      setNameError(true);
    } else {
      setNameError(false);
    }
  };

  const onSubmit = (data) => {
    if (!selectedName.trim()) {
      setNameError(true); // Set error if name is empty
      return;
    }

    dispatch(
      updateFormData({ ...data, name: selectedName, time: selectedTime })
    );
    dispatch(nextStep());
  };

  const isFormValid = !!selectedName.trim() && !!selectedTime;

  return (
    <>
      <Box>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col mb-5 justify-center "
        >
          <TextField
            label={t("Upload.Filename")}
            defaultValue={formData.name}
            {...register("name")}
            onChange={handleNameChange}
            error={nameError}
            helperText={nameError ? t("Upload.NameRequired") : ""}
            sx={{ mx: "auto", width: "90%" }}
          />
          <FormLabel
            component="h3"
            sx={{
              marginTop: ".7rem",
              marginBottom: ".3rem",
              fontSize: "16px",
              marginLeft: { md: "1.5rem" },
            }}
          >
              {t("Upload.save")}
          </FormLabel>

          <RadioGroup
            defaultValue={formData.time}
            {...register("time")}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              width: "100%",
              my: 1,
            }}
            onChange={handleRadioChange}
          >
            {[
              { value: 1, icon: <Hour1 />, label: t("Upload.Hour1"), desc: t("Upload.Hour1Desc")},
              { value: 6, icon: <Hour6 />, label: t("Upload.Hour6"), desc: t("Upload.Hour6Desc") },
              { value: 12, icon: <Hour12 />, label: t("Upload.Hour12"), desc: t("Upload.Hour12Desc") },
              { value: 24, icon: <Hour24 />, label: t("Upload.Hour24"), desc: t("Upload.Hour24Desc") },
            ].map(({ value, icon, label }) => (
              <motion.div
                key={value}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.5 }}
              >
                <FormControlLabel
                  value={value}
                  control={
                    <Radio
                      icon={icon}
                      checkedIcon={React.cloneElement(icon, {
                        sx: { color: "white" },
                      })}
                    />
                  }
                  labelPlacement="start"
                  sx={{
                    display: "flex",
                    flexDirection: "row-reverse",
                    alignItems: "center",
                    borderRadius: 2,
                    ml:0,
                    px: 2,
                    py: 1.5,
                    boxShadow: String(selectedTime) === String(value) ? 3 : 1,
                    border:
                      String(selectedTime) === String(value)
                        ? "2px solid #14b8a6"
                        : "1px solid #e0e0e0",
                    backgroundColor:
                      String(selectedTime) === String(value)
                        ? 'secondary.main'
                        : "background.paper",
                    color:
                      String(selectedTime) === String(value)
                        ? "white"
                        : "text.primary",
                    transition: "all 0.2ms ease-in-out",
                    "&:hover": {
                      boxShadow: 4,
                      borderColor: String(selectedTime) === String(value) ? "#14b8a6" : "#cfd8dc",
                    },
                    "& .MuiFormControlLabel-label": {
                      fontSize: "18px",
                      fontWeight: 600,
                    },
                  }}
                  label={label}
                  {...register("time")}
                  className={
                    String(selectedTime) === String(value)
                      ? "selected-radio flex bg-teal  p-2 rounded-lg text-ice-white justify-between w-full"
                      : "flex  p-2 rounded-lg border-2 justify-between w-full"
                  }
                />
             
              </motion.div>
            ))}
          </RadioGroup>
          <StepNavigation
            key={i18n.language}
            onBack={() => dispatch(prevStep())}
            onContinue={handleSubmit(onSubmit)}
            backLabel={t("Upload.Back")}
            continueLabel={t("Upload.Continue")}
            showBack={true}
            showContinue={true}
            continueDisabled={!isFormValid}
          />
        </form>
      </Box>
    </>
  );
}

export default FileName;
